import type { ApolloError } from '@apollo/client';
import type { GraphQLError } from 'graphql';

/**
 * Extracts the most meaningful error message from a GraphQL mutation response.
 *
 * Checks (in order):
 * 1. GraphQL errors returned in `res.errors` (most specific — backend validation messages)
 * 2. Apollo network errors (connection issues, 4xx/5xx)
 * 3. Falls back to the provided fallback message
 */
export function extractGraphQLError(
    errors: readonly GraphQLError[] | undefined,
    fallbackMessage: string,
): string {
    if (errors && errors.length > 0) {
        // Return the first GraphQL error message — these are typically the most
        // actionable messages (e.g. "Enter a valid phone number", "Email already exists")
        const message = errors[0].message;
        if (message && message.trim().length > 0) {
            return message;
        }
    }
    return fallbackMessage;
}

/**
 * Extracts an error message from a caught Apollo error (from .catch()).
 * Handles both GraphQL errors and network errors.
 */
export function extractCaughtError(
    error: unknown,
    fallbackMessage: string,
): string {
    if (error && typeof error === 'object' && 'graphQLErrors' in error) {
        const apolloError = error as ApolloError;
        if (apolloError.graphQLErrors?.length > 0) {
            const message = apolloError.graphQLErrors[0].message;
            if (message && message.trim().length > 0) {
                return message;
            }
        }
        if (apolloError.networkError) {
            // Network errors with result body (e.g. 400 responses)
            const networkErr = apolloError.networkError as any;
            if (networkErr.result?.errors?.length > 0) {
                return networkErr.result.errors[0].message || fallbackMessage;
            }
        }
    }
    return fallbackMessage;
}
